// Delivery-pipeline client hook. Owns fetch / group / import-csv / patch
// against /api/sessions/{id}/delivery-analysis.
//
// Auto-loads on mount. Does NOT auto-trigger grouping — the user runs
// AI grouping explicitly via the wizard at import time or the "Re-group"
// button. This matches the refocus to "MUs come from CSV; AI is opt-in."

import { useCallback, useEffect, useState } from 'react';
import type {
  DeliveryAnalysisPayload,
  DeliveryMutation,
  DiscoveryFeature,
  ImportCsvResult,
  MigrationUnit,
  MuMode,
} from './sessionTypes';

const EMPTY_PAYLOAD: DeliveryAnalysisPayload = {
  importedFeatures: [],
  migrationUnits: [],
  featureScoring: {},
  scoringManualFields: {},
  scoringGeneratedAt: null,
  hidden: {},
  basedOnExtractedAt: null,
  basedOnImportHash: null,
  warnings: [],
  generatedAt: null,
  status: 'idle',
};

export interface UseDeliveryAnalysisReturn {
  payload: DeliveryAnalysisPayload;
  loading: boolean;
  grouping: boolean;
  scoring: boolean;
  importing: boolean;
  generatingFlow: Record<string, boolean>;
  error: string | null;
  refresh: () => Promise<void>;
  group: () => Promise<void>;
  score: (opts?: { fromScratch?: boolean }) => Promise<void>;
  generateFlow: (muName: string) => Promise<void>;
  importCsv: (
    rows: object[],
    opts: { muMode: MuMode; force?: boolean; singleMuName?: string },
  ) => Promise<ImportCsvResult | null>;
  patch: (mutation: DeliveryMutation) => Promise<void>;
}

interface Options {
  sessionId: string | null;
}

export function useDeliveryAnalysis(opts: Options): UseDeliveryAnalysisReturn {
  const { sessionId } = opts;
  const [payload, setPayload] = useState<DeliveryAnalysisPayload>(EMPTY_PAYLOAD);
  const [loading, setLoading] = useState(false);
  const [grouping, setGrouping] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [importing, setImporting] = useState(false);
  const [generatingFlow, setGeneratingFlow] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/delivery-analysis`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPayload(normalizePayload(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load delivery analysis');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const group = useCallback(async () => {
    if (!sessionId) return;
    setGrouping(true);
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/delivery-analysis/group`, { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPayload(normalizePayload(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Grouping failed');
    } finally {
      setGrouping(false);
    }
  }, [sessionId]);

  const score = useCallback(async (scoreOpts: { fromScratch?: boolean } = {}) => {
    if (!sessionId) return;
    setScoring(true);
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/delivery-analysis/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromScratch: !!scoreOpts.fromScratch }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPayload(normalizePayload(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scoring failed');
    } finally {
      setScoring(false);
    }
  }, [sessionId]);

  const generateFlow = useCallback(async (muName: string) => {
    if (!sessionId) return;
    setGeneratingFlow((prev) => ({ ...prev, [muName]: true }));
    setError(null);
    try {
      const res = await fetch(
        `/api/sessions/${sessionId}/delivery-analysis/units/${encodeURIComponent(muName)}/flow`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPayload(normalizePayload(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Flow generation failed');
    } finally {
      setGeneratingFlow((prev) => {
        const next = { ...prev };
        delete next[muName];
        return next;
      });
    }
  }, [sessionId]);

  const importCsv = useCallback(async (
    rows: object[],
    importOpts: { muMode: MuMode; force?: boolean; singleMuName?: string },
  ): Promise<ImportCsvResult | null> => {
    if (!sessionId) return null;
    setImporting(true);
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/delivery-analysis/import-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rows,
          muMode: importOpts.muMode,
          force: !!importOpts.force,
          singleMuName: importOpts.singleMuName,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const normalized = normalizePayload(data);
      setPayload(normalized);
      return {
        imported: data.imported || 0,
        duplicates: data.duplicates || [],
        rejected: data.rejected || [],
        payload: normalized,
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CSV import failed');
      return null;
    } finally {
      setImporting(false);
    }
  }, [sessionId]);

  const patch = useCallback(async (mutation: DeliveryMutation) => {
    if (!sessionId) return;
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/delivery-analysis`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mutation }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPayload(normalizePayload(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  }, [sessionId]);

  useEffect(() => { refresh(); }, [refresh]);

  return {
    payload, loading, grouping, scoring, importing, generatingFlow, error,
    refresh, group, score, generateFlow, importCsv, patch,
  };
}

function normalizePayload(data: any): DeliveryAnalysisPayload {
  const importedFeatures = Array.isArray(data?.importedFeatures) ? (data.importedFeatures as DiscoveryFeature[]) : [];
  const migrationUnits = Array.isArray(data?.migrationUnits)
    ? (data.migrationUnits as MigrationUnit[]).map((m) => ({
        name: m.name,
        description: m.description || '',
        comments: m.comments || '',
        dependsOn: Array.isArray(m.dependsOn) ? m.dependsOn : [],
        featureIds: Array.isArray(m.featureIds) ? m.featureIds : [],
        flowText: m.flowText || '',
        flowGeneratedAt: m.flowGeneratedAt ?? null,
      }))
    : [];
  return {
    importedFeatures,
    migrationUnits,
    featureScoring: (data?.featureScoring && typeof data.featureScoring === 'object') ? data.featureScoring : {},
    scoringManualFields: (data?.scoringManualFields && typeof data.scoringManualFields === 'object') ? data.scoringManualFields : {},
    scoringGeneratedAt: data?.scoringGeneratedAt ?? null,
    hidden: data?.hidden || {},
    basedOnExtractedAt: data?.basedOnExtractedAt ?? null,
    basedOnImportHash: data?.basedOnImportHash ?? null,
    warnings: Array.isArray(data?.warnings) ? data.warnings : [],
    generatedAt: data?.generatedAt ?? null,
    status: (data?.status as 'idle' | 'pending' | 'error') || 'idle',
  };
}
